import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";

// Landing from the 48h buyer nudge email: one click forwards the original campaign RFQ to 2 more suppliers.
export default function RFQResendPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/rfq/resend/${id}?t=${encodeURIComponent(t)}`).then(({ data }) => setInfo(data)).catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired"));
  }, [id, t]);

  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/rfq/resend/${id}`, { t }); setResult(data); }
    catch (e) { setError(e.response?.data?.detail || "Could not forward your request"); }
    finally { setBusy(false); }
  };

  const resentTo = result?.resent_to || info?.resent_to || [];
  const done = Boolean(result) || Boolean(info?.resent_at);

  return (
    <div className="min-h-[70vh] bg-[#F7F6F2] px-4 py-16" data-testid="rfq-resend-page">
      <Helmet><title>Send your quote request to more suppliers · Accommodation Suppliers</title><meta name="robots" content="noindex" /></Helmet>
      <div className="max-w-xl mx-auto bg-white border border-[#E8ECE9] p-8 md:p-10">
        <div className="tracking-overline mb-3">Your quote request</div>
        {error && <div className="text-sm text-[#B35C4A]" data-testid="rfq-resend-error">{error}</div>}
        {!error && !info && <div className="flex items-center gap-2 text-sm text-[#7D8C85]"><Loader2 className="w-4 h-4 animate-spin" /> Loading your request…</div>}
        {info && !done && (
          <>
            <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] leading-tight">Send your request for {info.product_name || "linen"} to {info.resend_count} more suppliers</h1>
            <p className="text-sm text-[#4A5550] mt-4 leading-relaxed">Hi {info.customer_name || "there"} — your original request went to <strong>{info.suppliers_contacted.join(", ")}</strong>. We'll forward exactly the same details to {info.resend_count} more verified suppliers in the same category. Nothing to fill in again.</p>
            {info.any_replied && <p className="text-xs text-[#264839] mt-3" data-testid="rfq-resend-replied-note">Good news — one of the original suppliers has since marked your request as replied. You can still send to more suppliers if you'd like extra quotes.</p>}
            <button type="button" onClick={send} disabled={busy} className="btn-primary mt-6 disabled:opacity-60" data-testid="rfq-resend-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {busy ? "Sending…" : `Send to ${info.resend_count} more suppliers`}
            </button>
          </>
        )}
        {info && done && (
          <div data-testid="rfq-resend-done">
            <CheckCircle2 className="w-8 h-8 text-[#264839]" />
            <h1 className="font-serif text-3xl text-[#11211A] mt-3">Sent to {resentTo.length} more supplier{resentTo.length === 1 ? "" : "s"}</h1>
            <ul className="mt-4 text-sm text-[#11211A] space-y-1">{resentTo.map((n) => <li key={n} data-testid="rfq-resend-recipient">· {n}</li>)}</ul>
            <p className="text-sm text-[#4A5550] mt-4">They'll reply directly to your inbox — most respond within 24–48 hours. A confirmation is on its way to you now.</p>
            {result?.already && <p className="text-xs text-[#7D8C85] mt-2">This link had already been used, so nothing new was sent.</p>}
          </div>
        )}
        <div className="mt-8 pt-6 border-t border-[#E8ECE9] text-xs text-[#7D8C85]">Need something else? <Link to="/get-quotes" className="text-[#264839] underline underline-offset-2" data-testid="rfq-resend-new-link">Start a new quote request</Link> or reply to our email — hello@accommodationsuppliers.com.</div>
      </div>
    </div>
  );
}
